import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface ContextLayer {
  label?: ReactNode;
  actions?: ReactNode;
}

interface ContextData {
  items: ContextLayer[];
  push: (item: ContextLayer) => ContextLayer;
  remove: (item: ContextLayer) => void;
  update: (item: ContextLayer, value: ContextLayer) => ContextLayer;
}

const NULL: ContextData = {
  items: [],
  push: (item) => item,
  remove: () => {},
  update: (item, value) => value,
};

export const Context = createContext<ContextData>(NULL);

export const useHeaderProvider = () => {
  const [items, setItems] = useState<ContextLayer[]>([]);
  const push = useCallback(
    (value: ContextLayer) => {
      setItems((current) => [...current, value]);
      return value;
    },
    [setItems],
  );
  const update = useCallback(
    (item: ContextLayer, value: ContextLayer) => {
      setItems((current) => current.map((i) => (item === i ? value : i)));
      return value;
    },
    [setItems],
  );
  const remove = useCallback(
    (item: ContextLayer) => {
      setItems((current) => current.filter((i) => i !== item));
    },
    [setItems],
  );
  return useMemo(
    () =>
      ({
        items,
        push,
        update,
        remove,
      }) as ContextData,
    [items, push, update, remove],
  );
};

export const useHeaderUpdater = () => {
  const { push, update, remove } = useContext(Context);
  return useMemo(() => ({ push, update, remove }), [push, update, remove]);
};

export const useHeaderConsumer = () => {
  const { items } = useContext(Context);
  const result = items.reduce((result, item) => ({ ...result, ...item }), {} as ContextLayer);
  return result;
};
